import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// material-ui
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import GroupsIcon from '@mui/icons-material/Groups';
import InsightsIcon from '@mui/icons-material/Insights';

import UserListV2 from './UserListV2';
import AddToCampaignModal from '../InfluencerList/AddToCampaignModal';
import AudienceUniqueReachModal from './AudienceUniqueReachModal';
import CreatorTrustModal from './CreatorTrustModal';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ListIcon from '../../../../assets/discovery/list-icon.svg';
import emptyImg from '../../../../assets/discovery/empty.jpg';
import { getPlannerDownload } from 'api_handlers/discovery';
import { showPremiumFeatureDialog, fetchInfluencersAction, updatePage } from 'actions/planner2';
import { updateFilters } from 'actions/planner-filters';
import mixpanel from '../../../../utils/mixpanel';

const scrollToTop = async () => {
  window.scrollTo({ top: 0, behavior: 'auto' });
};

// Discover-only, redesigned list container. Mirrors the data flow of the
// original InfluencerList (redux page + fetch on pagination, category sort)
// but presents it with the professional V2 surface and the enriched table.
const InfluencerListV2 = ({
  influencers,
  totalResults,
  loading,
  isDiscover,
  useCategorySort,
  filters,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.planner2.user);
  const page = useSelector((state) => state.planner2.page);
  const selectedInfluencers = useSelector((state) => state.planner2.selectedInfluencers);

  const [sortBy, setSortBy] = React.useState(useCategorySort ? 'category' : 'followers');
  const [openAddToCampaign, setOpenAddToCampaign] = React.useState(false);
  const [openModalCondition, setOpenModalCondition] = useState('');
  const [addToCampaignInfluencers, setAddToCampaignInfluencers] = React.useState([]);
  const [isSortApplied, setIsSortApplied] = useState(false);
  const [openReach, setOpenReach] = useState(false);
  const [openTrust, setOpenTrust] = useState(false);

  const handleSortByChange = (event) => {
    setSortBy(event.target.value);
  };

  useEffect(() => {
    dispatch(updateFilters('sortBy', sortBy, true, true));
  }, [sortBy, dispatch]);

  // Preserve the original auto sort behaviour when advanced filters change.
  useEffect(() => {
    if (isDiscover) {
      if (
        isSortApplied === false &&
        (Object.keys(filters).length > 6 ||
          filters.bio ||
          filters.category ||
          filters.ids ||
          filters.posts)
      ) {
        setSortBy('followers');
        setIsSortApplied(true);
      } else if (filters.platform === 'youtube') {
        setIsSortApplied(true);
      } else if (
        isSortApplied === true &&
        filters.sortBy === 'category' &&
        Object.keys(filters).length === 6 &&
        filters.platform === 'instagram' &&
        filters.bio === null &&
        filters.category === null &&
        filters.ids === null &&
        filters.posts === null
      ) {
        setSortBy('category');
        setIsSortApplied(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleDownload = () => {
    getPlannerDownload({
      selected_ids: Array.from(selectedInfluencers),
      objective: '',
      prediction: {},
      contacts: 'not_req',
      selectedColumns: [],
    });
  };

  const handleAddToCampaign = (influencerIds, isAuth) => {
    setOpenModalCondition(isAuth);
    const infs = influencers.filter((inf) => influencerIds.includes(inf.id));
    setAddToCampaignInfluencers(infs);
    setOpenAddToCampaign(true);
  };

  const hasResults = influencers && influencers.length > 0;
  const showEmpty = !loading && (totalResults == null || totalResults === 0);

  return (
    <Box className="dv2-surface" sx={{ overflow: 'hidden' }}>
      {/* header */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #eef0f5',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '10px',
              background: 'linear-gradient(135deg,#111936,#26315f)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <GroupsIcon sx={{ color: '#febd1c', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.02rem', color: '#1f2433', lineHeight: 1.1 }}>
              {filters?.platform ? `${filters.platform[0].toUpperCase()}${filters.platform.slice(1)}` : ''} Creators
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
              {showEmpty
                ? 'Refine the filters to discover creators'
                : `${parseInt(totalResults || 0).toLocaleString('en-IN')} matching creators`}
            </Typography>
          </Box>
        </Box>

        <FormControl size="small" sx={{ minWidth: 150, '& .MuiInputLabel-root': { top: '2px' } }}>
          <InputLabel id="dv2-sort">Sort by</InputLabel>
          <Select labelId="dv2-sort" value={sortBy} onChange={handleSortByChange} label="Sort by">
            {useCategorySort && <MenuItem value={'category'}>Relevance</MenuItem>}
            <MenuItem value={'followers'}>Followers</MenuItem>
            <MenuItem value={'views'}>Avg Views</MenuItem>
            <MenuItem value={'eng-rate'}>Engagement Rate</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* body */}
      {isDiscover && loading && !hasResults ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 7 }}>
          <CircularProgress sx={{ color: '#febd1c' }} />
          <Typography sx={{ ml: 2, color: '#6b7280' }}>Finding the best creators…</Typography>
        </Box>
      ) : showEmpty && !hasResults ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <img src={emptyImg} alt="empty" style={{ width: 220, opacity: 0.9 }} />
          <Typography variant="h5" sx={{ mt: 2, color: '#1f2433', fontWeight: 700 }}>
            Start your search
          </Typography>
          <Typography sx={{ color: '#6b7280', mt: 0.5 }}>
            Enter details in the filters above to find influencers.
          </Typography>
        </Box>
      ) : (
        <UserListV2 influencers={influencers} loading={loading} handleAddToCampaign={handleAddToCampaign} />
      )}

      {/* pagination */}
      {hasResults ? (
        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', borderTop: '1px solid #eef0f5' }}>
          <Pagination
            count={Math.ceil((totalResults || 0) / 20)}
            color="primary"
            page={page}
            onChange={(e, updatedPage) => {
              if (user.premium_user || updatedPage < 4) {
                dispatch(updatePage(updatedPage));
                dispatch(fetchInfluencersAction(updatedPage));
                scrollToTop();
                if (process.env.NODE_ENV === 'production') {
                  mixpanel.track('Discover - Page clicked', { page: updatedPage });
                }
              } else dispatch(showPremiumFeatureDialog(true));
            }}
          />
        </Box>
      ) : null}

      {/* selection action bar */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={selectedInfluencers.size > 0 && !openAddToCampaign}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: '#111936',
            borderRadius: 2,
            boxShadow: '0 16px 40px -12px rgba(17,25,54,0.6)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'nowrap',
          }}
        >
          <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700, whiteSpace: 'nowrap', mr: 1 }}>
            Selected {selectedInfluencers.size} influencers
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ whiteSpace: 'nowrap' }}
            onClick={() => handleAddToCampaign(Array.from(selectedInfluencers), 'Planner')}
            startIcon={<img src={ListIcon} style={{ width: '12px' }} alt="" />}
          >
            Add to List
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ whiteSpace: 'nowrap' }}
            onClick={() => {
              if (user.premium_user) {
                handleAddToCampaign(Array.from(selectedInfluencers), 'AddToCampaign');
              } else {
                dispatch(showPremiumFeatureDialog(true));
              }
            }}
            startIcon={<WorkspacePremiumIcon />}
          >
            Add to Campaign
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ whiteSpace: 'nowrap' }}
            onClick={() => {
              if (user.premium_user) {
                handleDownload();
              } else {
                dispatch(showPremiumFeatureDialog(true));
              }
            }}
            startIcon={<WorkspacePremiumIcon />}
          >
            Download
          </Button>
          {selectedInfluencers.size >= 2 && selectedInfluencers.size <= 10 && (
            <Tooltip title="Estimate unique vs redundant audience across the selected creators">
              <Button
                variant="contained"
                color="primary"
                sx={{ whiteSpace: 'nowrap' }}
                onClick={() => setOpenReach(true)}
                startIcon={<InsightsIcon />}
              >
                Unique Reach
              </Button>
            </Tooltip>
          )}
          {selectedInfluencers.size === 1 && (
            <Tooltip title="Run the Creator Trust vetting (gates + evidence) on this creator">
              <Button
                variant="contained"
                color="primary"
                sx={{ whiteSpace: 'nowrap' }}
                onClick={() => setOpenTrust(true)}
                startIcon={<VerifiedUserIcon />}
              >
                Trust Check
              </Button>
            </Tooltip>
          )}
        </Box>
      </Snackbar>

      <AudienceUniqueReachModal
        open={openReach}
        setOpen={setOpenReach}
        ids={Array.from(selectedInfluencers)}
      />

      <CreatorTrustModal
        open={openTrust}
        setOpen={setOpenTrust}
        id={Array.from(selectedInfluencers)[0]}
      />

      <AddToCampaignModal
        v2
        open={openAddToCampaign}
        setOpen={setOpenAddToCampaign}
        influencers={addToCampaignInfluencers}
        text={openModalCondition}
      />
    </Box>
  );
};

export default InfluencerListV2;
